import { createSelector, createSlice } from '@reduxjs/toolkit';
import serviceWorkerEventItemData from '../../public/serviceworkeritem.json';

// 他のSliceのアクションタイプを文字列定数として定義し、循環参照を避ける
const DELETE_SCRIPT_ACTION = 'scriptItem/deleteScript';
const UPDATE_SCRIPT_ACTION = 'scriptItem/updateScriptItem';

const initialState = {
  items: [],
};

const serviceWorkerItemSlice = createSlice({
  name: 'serviceWorkerItem', // 'serviceWorkderEventItem' からタイポを修正
  initialState,
  reducers: {
    /**
     * 初期データを state にロードします。
     * @param {object} state - 現在の state。
     * @param {object} action - Redux アクション。ペイロードにデータが含まれていればそれを使用し、なければデフォルトのJSONデータをロードします。
     */
    loadServiceWorkerItemData(state, action) {
      const payload = action.payload;

      let loadedItems = [];
      if (Array.isArray(payload)) {
        loadedItems = payload;
      } else {
        loadedItems = serviceWorkerEventItemData || [];
      }

      state.items = loadedItems
        .filter(item => item && typeof item === 'object')
        .map(item => {
          const props = (item.properties && typeof item.properties === 'object') ? item.properties : {};

          return {
            eventName: item.eventName || 'unknown',
            ...item,
            properties: {
              listenerTarget: "self",
              comment: "",
              ...props,
              // 【重要】スクリプト名は文字列のみ許可
              scripts: Array.isArray(props.scripts)
                ? props.scripts.filter(s => s && typeof s === 'string')
                : []
            }
          };
        });
    },

    /**
     * Service Worker のイベント定義を追加または更新します (Upsert)。
     * AIエージェントからの `upsertServiceWorkerEvent` コマンドに対応します。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには { eventName, comment, scripts } を含みます。
     */
    upsertServiceWorkerEvent(state, action) {
      const { eventName, comment, scripts } = action.payload;
      const existingEvent = state.items.find(item => item.eventName === eventName);

      if (existingEvent) {
        // イベントが存在する場合、プロパティを更新
        if (comment !== undefined) {
          existingEvent.properties.comment = comment;
        }
        if (scripts !== undefined && Array.isArray(scripts)) {
          existingEvent.properties.scripts = scripts;
        }
      } else {
        // イベントが存在しない場合、新しいイベントを追加
        state.items.push({
          eventName: eventName,
          properties: {
            listenerTarget: "self",
            scripts: scripts || [],
            comment: comment || `Custom event: ${eventName}`
          }
        });
      }
    },

    /**
     * 新しい Service Worker イベントを追加します。
     * `upsertServiceWorkerEvent` の利用を推奨しますが、UIからの直接操作のために残します。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには追加する `event` オブジェクトを含みます。
     */
    addServiceWorkerEvent(state, action) {
      const { event } = action.payload;
      const exists = state.items.some(item => item.eventName === event.eventName);
      if (!exists) {
        state.items.push(event);
      }
    },

    /**
     * 指定された Service Worker イベントを削除します。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには削除するイベントの `eventName` を含みます。
     */
    deleteServiceWorkerEvent(state, action) {
      const { eventName } = action.payload;
      state.items = state.items.filter(item => item.eventName !== eventName);
    },

    /**
     * イベントリスト内のイベントの順序を変更します。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには { eventName, newIndex } を含みます。
     */
    changeServiceWorkerEventOrder(state, action) {
      const { eventName, newIndex } = action.payload;
      const eventIndex = state.items.findIndex(item => item.eventName === eventName);
      if (eventIndex > -1) {
        const [removedItem] = state.items.splice(eventIndex, 1);
        state.items.splice(newIndex, 0, removedItem);
      }
    },

    /**
     * 既存の Service Worker イベントの名前とコメントを編集します。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには { eventTargetName, newEventName, comment } を含みます。
     */
    modifyServiceWorkerEvent(state, action) {
      const { eventTargetName, newEventName, comment } = action.payload;
      const eventItem = state.items.find(item => item.eventName === eventTargetName);
      if (eventItem) {
        if (newEventName) {
          eventItem.eventName = newEventName;
        }
        if (comment !== undefined) {
          eventItem.properties.comment = comment;
        }
      }
    },

    /**
     * 指定された Service Worker イベントにスクリプトを紐付けます。
     * 既に紐付けられている場合は重複して追加しません。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには { eventName, scriptName } を含みます。
     */
    addScriptToServiceWorkerEvent(state, action) {
      const { eventName, scriptName } = action.payload;
      const eventItem = state.items.find(item => item.eventName === eventName);
      if (eventItem && !eventItem.properties.scripts.includes(scriptName)) {
        eventItem.properties.scripts.push(scriptName);
      }
    },

    /**
     * 指定された Service Worker イベントからスクリプトの紐付けを解除します。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには { eventName, scriptName } を含みます。
     */
    deleteServiceWorkerEventScript(state, action) {
      const { eventName, scriptName } = action.payload;
      const eventItem = state.items.find(item => item.eventName === eventName);
      if (eventItem) {
        eventItem.properties.scripts = eventItem.properties.scripts.filter(script => script !== scriptName);
      }
    },

    /**
     * 全てのイベントを走査し、変更されたスクリプト名を新しい名前に更新します。
     * UI操作でのスクリプト名変更時に使用します。
     * @param {object} state - 現在の state。
     * @param {object} action - ペイロードには { scriptName, newScriptName } を含みます。
     */
    updateServiceWorkerEventScriptName(state, action) {
      const { scriptName, newScriptName } = action.payload;
      state.items.forEach(item => {
        const scriptIndex = item.properties.scripts.indexOf(scriptName);
        if (scriptIndex > -1) {
          item.properties.scripts[scriptIndex] = newScriptName;
        }
      });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(DELETE_SCRIPT_ACTION, (state, action) => {
        // ScriptItemSliceのdeleteScriptアクションがディスパッチされたときに実行
        const deletedScriptName = action.payload; // deleteScriptはscriptNameをペイロードとして渡すと仮定
        if (deletedScriptName) {
          state.items.forEach(item => {
            item.properties.scripts = item.properties.scripts.filter(
              script => script !== deletedScriptName
            );
          });
        }
      })
      .addCase(UPDATE_SCRIPT_ACTION, (state, action) => {
        // ScriptItemSliceのupdateScriptItemアクションがディスパッチされたときに実行
        const { originScriptName, newScriptName } = action.payload;
        if (originScriptName && newScriptName && originScriptName !== newScriptName) {
          state.items.forEach(item => {
            const scriptIndex = item.properties.scripts.indexOf(originScriptName);
            if (scriptIndex > -1) {
              item.properties.scripts[scriptIndex] = newScriptName;
            }
          });
        }
      });
  }
});

/**
 * イベント名に基づいて Service Worker イベントオブジェクトを選択するセレクタ。
 */
export const selectServiceWorkerEventByEventName = createSelector(
  (state) => state.serviceworkerItems.items,
  (state, eventName) => eventName,
  (items, eventName) => {
    // itemsがundefinedの場合のフォールバックを追加
    return items ? items.find(item => item.eventName === eventName) : undefined;
  }
);

export const {
  loadServiceWorkerItemData,
  upsertServiceWorkerEvent,
  addServiceWorkerEvent,
  deleteServiceWorkerEvent,
  changeServiceWorkerEventOrder,
  modifyServiceWorkerEvent,
  addScriptToServiceWorkerEvent,
  deleteServiceWorkerEventScript,
  updateServiceWorkerEventScriptName,
} = serviceWorkerItemSlice.actions;

export default serviceWorkerItemSlice.reducer;